/**
 * A bulk lookup run, without React: looks a file's rows up a few at a time, sorts each answer into
 * found / not found / needs info / error, and makes the batch's finish counts and the CSV downloads.
 */
import type { BatchCounts, BatchOutcome, BulkRow, ProductLookup } from './api';

/** Rows looked up at once; each miss holds an agent call for ~20 s. */
export const BULK_CONCURRENCY = 3;

export type RowOutcome = 'found' | 'not_found' | 'needs_info' | 'error';

export interface RowResult {
  outcome: RowOutcome;
  /** The service's answer; null when the call itself failed. */
  answer: ProductLookup | null;
  /** Why it is not found, what the OEM needs, or what failed. */
  error: string | null;
}

/** The OEM did not know the serial (or it could not be asked); cached by the service for 7 days. */
const NEGATIVE = new Set(['not_found', 'oem_unknown', 'unsupported']);

export function outcomeOf(answer: ProductLookup): RowOutcome {
  if (answer.found) return 'found';
  if (answer.status && NEGATIVE.has(answer.status)) return 'not_found';
  if (answer.status === 'needs_more_info') return 'needs_info';
  return 'error';
}

/** A line on what went wrong (or why there is no unit); null for a plain answer. */
export function errorOf(answer: ProductLookup): string | null {
  const check = answer.check;
  if (answer.stale) return answer.refresh_error?.message || answer.refresh_error?.code || null;
  if (answer.status === 'needs_more_info') {
    const fields = check?.missing_fields ?? [];
    return fields.length > 0 ? `needs ${fields.join(', ')}` : 'needs more information';
  }
  if (answer.status === 'oem_unknown' || answer.status === 'unsupported') return answer.status;
  if (!answer.found && answer.status !== 'not_found') {
    return check?.error?.message || check?.error?.code || answer.status || 'no answer';
  }
  return null;
}

export function resultOf(answer: ProductLookup): RowResult {
  return { outcome: outcomeOf(answer), answer, error: errorOf(answer) };
}

export interface BulkRunOptions {
  concurrency?: number;
  /** Cancels the run: no row starts after it, and the rows in flight are aborted. */
  signal?: AbortSignal;
  onStart?: (index: number) => void;
  onResult?: (index: number, result: RowResult) => void;
}

export interface BulkRun {
  /** By row index; null for a row that was not looked up (the run was cancelled first). */
  results: Array<RowResult | null>;
  cancelled: boolean;
}

/**
 * Looks every row up with `lookup`, `concurrency` at a time, in file order. A failed call is an
 * error row, not a failed run; the run only stops early when `signal` aborts.
 */
export async function runBulkLookup(
  rows: readonly BulkRow[],
  lookup: (row: BulkRow, signal: AbortSignal) => Promise<ProductLookup>,
  { concurrency = BULK_CONCURRENCY, signal, onStart, onResult }: BulkRunOptions = {},
): Promise<BulkRun> {
  const results: Array<RowResult | null> = rows.map(() => null);
  const inner = new AbortController();
  const abort = () => inner.abort();
  if (signal?.aborted) inner.abort();
  else signal?.addEventListener('abort', abort, { once: true });

  let next = 0;
  const worker = async () => {
    while (!inner.signal.aborted && next < rows.length) {
      const index = next;
      next += 1;
      onStart?.(index);
      let result: RowResult;
      try {
        result = resultOf(await lookup(rows[index], inner.signal));
      } catch (error) {
        if (inner.signal.aborted) return;
        result = { outcome: 'error', answer: null, error: (error as Error)?.message || 'lookup failed' };
      }
      // An answer that lands after Cancel is still kept: the service has stored it.
      results[index] = result;
      onResult?.(index, result);
    }
  };
  try {
    await Promise.all(Array.from({ length: Math.min(Math.max(1, concurrency), rows.length) }, worker));
  } finally {
    signal?.removeEventListener('abort', abort);
  }
  return { results, cancelled: inner.signal.aborted };
}

/**
 * The batch's counts. `failed` is needs info and errors; `skipped` the rows not looked up;
 * `stored` the rows the OEM answered now (served from the agent, found or not found), which the
 * service stored with the batch. A cached answer was stored before, so it is not counted again.
 */
export function finishCounts(results: ReadonlyArray<RowResult | null>): BatchCounts {
  const counts: BatchCounts = { found: 0, not_found: 0, failed: 0, skipped: 0, stored: 0 };
  for (const result of results) {
    if (!result) {
      counts.skipped += 1;
      continue;
    }
    if (result.outcome === 'found') counts.found += 1;
    else if (result.outcome === 'not_found') counts.not_found += 1;
    else counts.failed += 1;
    if (result.answer?.served_from === 'agent' && (result.outcome === 'found' || result.outcome === 'not_found')) {
      counts.stored += 1;
    }
  }
  return counts;
}

/** Cancelled when cancelled; failed when no row got any answer (as the service judges an import). */
export function finishOutcome(run: BulkRun): BatchOutcome {
  if (run.cancelled) return 'cancelled';
  const done = run.results.filter((result): result is RowResult => result !== null);
  if (done.length > 0 && done.every((result) => result.outcome === 'error')) return 'failed';
  return 'completed';
}

// -- CSV -------------------------------------------------------------------------------------

function csvCell(value: unknown): string {
  let text = value === null || value === undefined ? '' : String(value);
  // A cell starting with = + - @ would run as a formula in a spreadsheet.
  if (/^[=+\-@\t\r]/.test(text) && !/^-?\d+(\.\d+)?$/.test(text)) text = `'${text}`;
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function toCsv(lines: ReadonlyArray<ReadonlyArray<unknown>>): string {
  return `${lines.map((line) => line.map(csvCell).join(',')).join('\n')}\n`;
}

const OUTCOME_TEXT: Record<RowOutcome, string> = {
  found: 'found',
  not_found: 'not found',
  needs_info: 'needs info',
  error: 'error',
};

export const RESULT_COLUMNS = ['Row', 'OEM', 'Serial number', 'Outcome', 'Product', 'Warranty status', 'End date', 'Error'];

/** Every row of the run (`failures`: needs info and errors only), with its answer. */
export function resultsCsv(rows: readonly BulkRow[], results: ReadonlyArray<RowResult | null>, failures = false): string {
  const lines = rows.flatMap((row, index) => {
    const result = results[index] ?? null;
    if (failures && !(result && (result.outcome === 'needs_info' || result.outcome === 'error'))) return [];
    const answer = result?.answer;
    return [[
      row.row,
      answer?.oem?.name || row.oem_text || row.oem,
      answer?.serial_number || row.serial_number,
      result ? OUTCOME_TEXT[result.outcome] : 'skipped',
      answer?.product?.product_name || answer?.product?.product_number,
      answer?.warranty?.status,
      answer?.warranty?.end_date,
      result?.error,
    ]];
  });
  return toCsv([RESULT_COLUMNS, ...lines]);
}

/** The blocked rows in the bulk file's own layout (OEM, then serial), to enrich locally and import. */
export function blockedCsv(rows: readonly BulkRow[]): string {
  return toCsv([['OEM', 'Serial number', 'Row'], ...rows.map((row) => [row.oem_text || row.oem, row.serial_number, row.row])]);
}

/** `list.xlsx` → `list-results.csv`. */
export function csvName(fileName: string, suffix: string): string {
  const base = fileName.replace(/\.[^./\\]*$/, '') || 'bulk-lookup';
  return `${base}-${suffix}.csv`;
}

/** Saves text as a file in the browser. */
export function downloadCsv(fileName: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** The file's SHA-256 (hex) for the batch record; null where the browser cannot hash it. */
export async function sha256Hex(file: Blob): Promise<string | null> {
  try {
    const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
  } catch {
    return null;
  }
}
